import React, { useState } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  InputLabel,
  Select,
  MenuItem,
  FormControl,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Avatar,
  InputAdornment,
  Snackbar,
  Alert
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Visibility,
  LocalShipping,
  Assignment,
  CheckCircle,
  Warning,
  Search,
  LocationOn,
  TrendingFlat,
  CalendarToday,
  ChevronRight
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const ORIGENES_DESTINOS = ['Explotación Local', 'Mercado Regional', 'Matadero Central', 'Pasto de Verano', 'Cliente Particular'];

const Movimientos = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    animal: 'todos',
    tipo: 'todos',
    estado: 'todos',
    origen: 'todos',
    destino: 'todos'
  });

  const { data: movimientosData, isLoading } = useQuery('movimientos', async () => {
    const response = await axios.get('/api/movimientos');
    return response.data.movimientos || [];
  });

  const { data: animalesData } = useQuery('animales', async () => {
    const response = await axios.get('/api/animales');
    return response.data;
  });

  const { data: guiasData } = useQuery('guias', async () => {
    const response = await axios.get('/api/guias');
    return response.data;
  });

  const [selectedMovimiento, setSelectedMovimiento] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newMovimientoDialog, setNewMovimientoDialog] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  
  const [formData, setFormData] = useState({
    animal_id: '',
    tipo_movimiento: 'traslado',
    fecha_movimiento: new Date().toISOString().split('T')[0],
    origen: '',
    destino: '',
    motivo: '',
    guia_numero: '',
    estado: 'programado'
  });

  const createMutation = useMutation(async (data) => await axios.post('/api/movimientos', data), {
    onSuccess: () => { queryClient.invalidateQueries('movimientos'); setNewMovimientoDialog(false); setSnackbarMessage('✅ Movimiento registrado'); setOpenSnackbar(true); }
  });

  const updateMutation = useMutation(async (data) => await axios.put(`/api/movimientos/${data.id}`, data), {
    onSuccess: () => { queryClient.invalidateQueries('movimientos'); setNewMovimientoDialog(false); setSnackbarMessage('✅ Registro actualizado'); setOpenSnackbar(true); }
  });

  const deleteMutation = useMutation(async (id) => await axios.delete(`/api/movimientos/${id}`), {
    onSuccess: () => { queryClient.invalidateQueries('movimientos'); setSnackbarMessage('🗑️ Registro eliminado'); setOpenSnackbar(true); }
  });

  const handleEdit = (mov) => {
    setFormData(mov);
    setIsEditing(true);
    setNewMovimientoDialog(true);
  };

  if (isLoading) return <Box sx={{ p: 4, mt: 8, textAlign: 'center' }}><Typography variant="h6">Cargando logística...</Typography></Box>;

  const movimientosList = Array.isArray(movimientosData) ? movimientosData : [];
  const animales = Array.isArray(animalesData) ? animalesData : [];
  const guias = Array.isArray(guiasData) ? guiasData : [];
  
  // Requirement 4: List movements 1, 2, 3 (Ascending)
  const sortedMovimientos = [...movimientosList].sort((a, b) => a.id - b.id);

  const filteredMovimientos = sortedMovimientos.filter(m => {
    const matchesSearch = (m.destino || '').toLowerCase().includes(searchTerm.toLowerCase()) || 
                         (m.guia_numero || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesAnimal = filters.animal === 'todos' || String(m.animal_id) === String(filters.animal);
    const matchesTipo = filters.tipo === 'todos' || m.tipo_movimiento === filters.tipo;
    const matchesEstado = filters.estado === 'todos' || m.estado === filters.estado;
    const matchesOrigen = filters.origen === 'todos' || m.origen === filters.origen;
    const matchesDestino = filters.destino === 'todos' || m.destino === filters.destino;

    return matchesSearch && matchesAnimal && matchesTipo && matchesEstado && matchesOrigen && matchesDestino;
  });

  const stats = {
    total: movimientosList.length,
    completados: movimientosList.filter(m => m.estado === 'completado').length,
    programados: movimientosList.filter(m => m.estado === 'programado').length,
    incidencias: movimientosList.filter(m => m.estado === 'cancelado').length
  };

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, mt: 8, bgcolor: 'var(--bg-app)', minHeight: '100vh' }}>
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 800, color: 'var(--navy-main)' }}>Logística y Trazabilidad</Typography>
          <Typography variant="body1" sx={{ color: 'var(--slate-light)' }}>Control de traslados y movimientos inter-explotación.</Typography>
        </Box>
        <Button
          className="luxury-button luxury-button-primary"
          variant="contained"
          startIcon={<Add />}
          onClick={() => { setIsEditing(false); setFormData({ animal_id: '', tipo_movimiento: 'traslado', fecha_movimiento: new Date().toISOString().split('T')[0], origen: '', destino: '', motivo: '', guia_numero: '', estado: 'programado' }); setNewMovimientoDialog(true); }}
        >
          Nuevo Movimiento
        </Button>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {[
          { label: 'Movimientos Totales', value: stats.total, color: 'var(--navy-main)', icon: <LocalShipping /> },
          { label: 'Efectuados', value: stats.completados, color: 'var(--emerald-main)', icon: <CheckCircle /> },
          { label: 'En Agenda', value: stats.programados, color: 'var(--alert-gold)', icon: <CalendarToday /> },
          { label: 'Incidencias', value: stats.incidencias, color: 'var(--alert-crimson)', icon: <Warning /> },
        ].map((s, i) => (
          <Grid item xs={12} sm={6} md={3} key={i}>
            <Card className="premium-card">
              <CardContent sx={{ p: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: `${s.color}15`, color: s.color, width: 48, height: 48 }}>{s.icon}</Avatar>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 800 }}>{s.value}</Typography>
                  <Typography variant="caption" sx={{ fontWeight: 800, color: 'var(--slate-light)', textTransform: 'uppercase' }}>{s.label}</Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Main Movements Registry */}
      <Card className="premium-card">
        <Box sx={{ p: 3, borderBottom: '1px solid var(--border-color)', display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Buscar por destino o Nº de Guía..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: <InputAdornment position="start"><Search /></InputAdornment>,
              sx: { borderRadius: '12px', bgcolor: 'var(--slate-bg)' }
            }}
          />
          <Grid container spacing={2}>
            <Grid item xs={6} md={2.4}>
              <FormControl fullWidth size="small">
                <InputLabel>Animal</InputLabel>
                <Select value={filters.animal} label="Animal" onChange={(e) => setFilters({...filters, animal: e.target.value})}>
                  <MenuItem value="todos">Todos</MenuItem>
                  {animales.map(a => <MenuItem key={a.id} value={a.id}>{a.crotal}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={2.4}>
              <FormControl fullWidth size="small">
                <InputLabel>Tipo</InputLabel>
                <Select value={filters.tipo} label="Tipo" onChange={(e) => setFilters({...filters, tipo: e.target.value})}>
                  <MenuItem value="todos">Todos</MenuItem>
                  <MenuItem value="traslado">Traslado</MenuItem>
                  <MenuItem value="venta">Venta</MenuItem>
                  <MenuItem value="compra">Compra</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={2.4}>
              <FormControl fullWidth size="small">
                <InputLabel>Origen</InputLabel>
                <Select value={filters.origen} label="Origen" onChange={(e) => setFilters({...filters, origen: e.target.value})}>
                  <MenuItem value="todos">Todos</MenuItem>
                  {ORIGENES_DESTINOS.map(o => <MenuItem key={o} value={o}>{o}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={2.4}>
              <FormControl fullWidth size="small">
                <InputLabel>Destino</InputLabel>
                <Select value={filters.destino} label="Destino" onChange={(e) => setFilters({...filters, destino: e.target.value})}>
                  <MenuItem value="todos">Todos</MenuItem>
                  {ORIGENES_DESTINOS.map(d => <MenuItem key={d} value={d}>{d}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={2.4}>
              <FormControl fullWidth size="small">
                <InputLabel>Estado</InputLabel>
                <Select value={filters.estado} label="Estado" onChange={(e) => setFilters({...filters, estado: e.target.value})}>
                  <MenuItem value="todos">Todos</MenuItem>
                  <MenuItem value="programado">Programado</MenuItem>
                  <MenuItem value="completado">Completado</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Box>
        <TableContainer>
          <Table>
            <TableHead sx={{ bgcolor: 'var(--slate-bg)' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 800 }}>ID / Animal</TableCell>
                <TableCell sx={{ fontWeight: 800 }}>Tipo & Guía</TableCell>
                <TableCell sx={{ fontWeight: 800 }}>Ruta</TableCell>
                <TableCell sx={{ fontWeight: 800 }}>Fecha & Estado</TableCell>
                <TableCell align="right" sx={{ fontWeight: 800 }}>Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredMovimientos.map((m) => (
                <TableRow key={m.id} hover>
                  <TableCell>
                    <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>#{m.id}</Typography>
                    <Typography variant="caption" sx={{ color: 'var(--slate-light)' }}>
                      {animales.find(a => String(a.id) === String(m.animal_id))?.crotal || '...'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip label={m.tipo_movimiento} size="small" variant="outlined" sx={{ fontWeight: 700 }} />
                    <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>Doc: {m.guia_numero || 'N/A'}</Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography variant="body2">{m.origen}</Typography>
                      <TrendingFlat fontSize="small" sx={{ color: 'var(--slate-light)' }} />
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>{m.destino}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{m.fecha_movimiento}</Typography>
                    <Chip label={m.estado} size="small" sx={{ 
                      height: '20px', fontSize: '10px', fontWeight: 800,
                      bgcolor: m.estado === 'completado' ? 'var(--alert-mint)' : '#fffbeb',
                      color: m.estado === 'completado' ? 'var(--emerald-main)' : 'var(--alert-gold)'
                    }} />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => { setSelectedMovimiento(m); setDialogOpen(true); }}><Visibility fontSize="small" /></IconButton>
                    <IconButton size="small" onClick={() => handleEdit(m)}><Edit fontSize="small" /></IconButton>
                    <IconButton size="small" color="error" onClick={() => { if(window.confirm('¿Eliminar?')) deleteMutation.mutate(m.id)}}><Delete fontSize="small" /></IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* New/Edit Dialog */}
      <Dialog open={newMovimientoDialog} onClose={() => setNewMovimientoDialog(false)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: '20px' } }}>
        <DialogTitle sx={{ fontWeight: 800 }}>{isEditing ? 'Editar Movimiento' : 'Programar Movimiento'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Seleccionar Animal</InputLabel>
                <Select value={formData.animal_id} label="Seleccionar Animal" onChange={(e) => setFormData({...formData, animal_id: e.target.value})}>
                  {animales.map(a => <MenuItem key={a.id} value={a.id}>{a.crotal} - {a.raza}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Tipo de Movimiento</InputLabel>
                <Select value={formData.tipo_movimiento} label="Tipo de Movimiento" onChange={(e) => setFormData({...formData, tipo_movimiento: e.target.value})}>
                  <MenuItem value="traslado">Traslado Interno</MenuItem>
                  <MenuItem value="venta">Venta</MenuItem>
                  <MenuItem value="compra">Compra</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Origen</InputLabel>
                <Select value={formData.origen} label="Origen" onChange={(e) => setFormData({...formData, origen: e.target.value})}>
                  {ORIGENES_DESTINOS.map(o => <MenuItem key={o} value={o}>{o}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Destino</InputLabel>
                <Select value={formData.destino} label="Destino" onChange={(e) => setFormData({...formData, destino: e.target.value})}>
                  {ORIGENES_DESTINOS.map(d => <MenuItem key={d} value={d}>{d}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Guía Asociada</InputLabel>
                <Select value={formData.guia_numero} label="Guía Asociada" onChange={(e) => setFormData({...formData, guia_numero: e.target.value})}>
                  <MenuItem value="">Sin guía</MenuItem>
                  {guias.map(g => <MenuItem key={g.id} value={g.codigo_guia}>{g.codigo_guia} ({g.tipo})</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth type="date" label="Fecha" InputLabelProps={{ shrink: true }} value={formData.fecha_movimiento} onChange={(e) => setFormData({...formData, fecha_movimiento: e.target.value})} />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Estado</InputLabel>
                <Select value={formData.estado} label="Estado" onChange={(e) => setFormData({...formData, estado: e.target.value})}>
                  <MenuItem value="programado">Programado</MenuItem>
                  <MenuItem value="completado">Completado</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setNewMovimientoDialog(false)}>Cancelar</Button>
          <Button variant="contained" className="luxury-button luxury-button-primary" onClick={() => isEditing ? updateMutation.mutate(formData) : createMutation.mutate(formData)}>
            {isEditing ? 'Actualizar' : 'Confirmar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Details Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '24px' } }}>
        <Box sx={{ p: 4 }}>
          <Typography variant="h5" sx={{ fontWeight: 800, mb: 3 }}>Detalle de Operación</Typography>
          <Grid container spacing={3}>
            {Object.entries({
              'Ref': `#MOV-${selectedMovimiento?.id}`,
              'Animal': animales.find(a => String(a.id) === String(selectedMovimiento?.animal_id))?.crotal,
              'Tipo': selectedMovimiento?.tipo_movimiento,
              'Desde': selectedMovimiento?.origen,
              'Hacia': selectedMovimiento?.destino,
              'Fecha': selectedMovimiento?.fecha_movimiento,
              'Guía': selectedMovimiento?.guia_numero || 'N/A',
              'Estado': selectedMovimiento?.estado
            }).map(([k, v]) => (
              <Grid item xs={6} key={k}>
                <Typography variant="caption" sx={{ fontWeight: 800, color: 'var(--slate-light)', textTransform: 'uppercase' }}>{k}</Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>{v}</Typography>
              </Grid>
            ))}
          </Grid>
          <Button fullWidth variant="outlined" sx={{ mt: 4, borderRadius: '12px' }} onClick={() => setDialogOpen(false)}>Cerrar</Button>
        </Box>
      </Dialog>

      <Snackbar open={openSnackbar} autoHideDuration={4000} onClose={() => setOpenSnackbar(false)}>
        <Alert severity="success" sx={{ width: '100%', borderRadius: '12px' }}>{snackbarMessage}</Alert>
      </Snackbar>
    </Box>
  );
};

export default Movimientos;
